// Vendor imports
const classNames = require('classnames')
const React = require('react')
const ReactCrop = require('react-image-crop').default
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const useClientEffect = require('client-v2/common/hooks/client-effect')
const tag = require('client-v2/common/utils/hyperscript')

const bem = BemEntity('UploadImageModalPreview')

function UploadImageModalPreview(props) {
  const { circularCrop, src } = props
  // Refs
  const imageRef = React.useRef()
  // State
  const [ crop, setCrop ] = React.useState({ x: 0, y: 0, unit: '%' })
  const [ minWidth, setMinWidth ] = React.useState()
  const [ minHeight, setMinHeight ] = React.useState()
  // Callbacks
  const onChange = useChangeCallback({ setCrop })
  const onComplete = useCompleteCallback({ imageRef, props })
  const onResize = useResizeCallback({ imageRef, props, setMinWidth, setMinHeight })
  const onImageLoaded = useImageLoadedCallback({ imageRef, props, setCrop, onResize })
  // Effects
  useResizeListener(onResize)
  // Render
  return (
    tag('section', {
      className: classNames(
        props.className,
        bem.block()
      )
    },
      !!props.src && (
        /** @see https://github.com/DominicTobias/react-image-crop#props */
        tag(ReactCrop, {
          circularCrop,
          crop,
          keepSelection: true,
          minWidth,
          minHeight,
          src,
          onChange,
          onComplete,
          onImageLoaded
        })
      )
    )
  )
}

// React hooks

function useChangeCallback({ setCrop }) {
  return React.useCallback(
    /** @see https://github.com/DominicTobias/react-image-crop#onchangecrop-percentcrop-required */
    (pxCrop, percentCrop) => {
      if (percentCrop.width && percentCrop.height) {
        setCrop(prevCrop => {
          /**
           * Attempt to mitigate `Error: Maximum update depth exceeded`
           * @see https://app.asana.com/0/102902769366319/1172851182015281/f
           */
          if (
            prevCrop.x != percentCrop.x ||
            prevCrop.y != percentCrop.y ||
            prevCrop.width != percentCrop.width ||
            prevCrop.height != percentCrop.height
          ) {
            return percentCrop
          }
          return prevCrop
        })
      }
    },
    [ setCrop ]
  )
}

function useCompleteCallback({ imageRef, props }) {
  const { minWidth, minHeight, onCropped } = props
  return React.useCallback(
    /** @see https://github.com/DominicTobias/react-image-crop#oncompletecrop-percentcrop-optional */
    (pxCrop, percentCrop) => {
      const image = imageRef.current
      if (image && percentCrop.width && percentCrop.height) {
        const denormalizedCrop = {
          x: Math.round(image.naturalWidth * (percentCrop.x / 100)),
          y: Math.round(image.naturalHeight * (percentCrop.y / 100)),
          width: Math.round(image.naturalWidth * (percentCrop.width / 100)),
          height: Math.round(image.naturalHeight * (percentCrop.height / 100))
        }

        // Ensure imprecision in denormalized percentage crop doesn't violate minumum dimensions
        denormalizedCrop.width = Math.max(minWidth || 0, denormalizedCrop.width)
        denormalizedCrop.height = Math.max(minHeight || 0, denormalizedCrop.height)

        onCropped(denormalizedCrop)
      }
    },
    [ imageRef, minWidth, minHeight, onCropped ]
  )
}

function useImageLoadedCallback({ imageRef, props, setCrop, onResize }) {
  const { resizeWidth, resizeHeight } = props
  return React.useCallback(
    /** @see https://github.com/DominicTobias/react-image-crop#onimageloadedimage-optional */
    (image) => {
      imageRef.current = image
      // Calculate aspect crop
      const aspectRatio = resizeWidth / resizeHeight
      const aspectWidth = Math.round(image.height * aspectRatio)
      const aspectHeight = Math.round(image.width / aspectRatio)
      // Calculate center
      let xPixels = 0
      let yPixels = 0
      if (aspectWidth < image.width) {
        xPixels = Math.round((image.width / 2) - (aspectWidth / 2))
      } else if (aspectHeight < image.height) {
        yPixels = Math.round((image.height / 2) - (aspectHeight / 2))
      }
      // Convert pixels to percentages
      const xPercent = (xPixels / image.width) * 100
      const yPercent = (yPixels / image.height) * 100
      /**
       * Set default crop based on image dimensions
       * @see https://github.com/DominicTobias/react-image-crop#crop-required
       */
      setCrop({
        x: xPercent,
        y: yPercent,
        width: 100,
        aspect: aspectRatio,
        unit: '%'
      })
      onResize()
      return false
    },
    [ imageRef, resizeWidth, resizeHeight, setCrop, onResize ]
  )
}

function useResizeCallback({ imageRef, props, setMinWidth, setMinHeight }) {
  const { minWidth, minHeight } = props
  return React.useCallback(
    () => {
      const image = imageRef.current
      if (image) {
        /**
         * Calculate minimum crop width (in pixels) based on the image width in the DOM
         * @see https://github.com/DominicTobias/react-image-crop#minwidth-optional
         */
        let computedMinWidth
        if (minWidth) {
          computedMinWidth = Math.round(minWidth * image.width / image.naturalWidth)
        }
        setMinWidth(computedMinWidth)
        /**
         * Calculate minimum crop height (in pixels) based on the image height in the DOM
         * @see https://github.com/DominicTobias/react-image-crop#minheight-optional
         */
        let computedMinHeight
        if (minHeight) {
          computedMinHeight = Math.round(minHeight * image.height / image.naturalHeight)
        }
        setMinHeight(computedMinHeight)
      }
    },
    [ imageRef, minWidth, minHeight, setMinWidth, setMinHeight ]
  )
}

function useResizeListener(onResize) {
  useClientEffect(
    () => {
      window.addEventListener('resize', onResize)
      return () => {
        window.removeEventListener('resize', onResize)
      }
    },
    [ onResize ]
  )
}

// Prop-types

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  UploadImageModalPreview.propTypes = {
    // Values
    circularCrop: PropTypes.bool,
    className: PropTypes.string,
    minWidth: PropTypes.number,
    minHeight: PropTypes.number,
    resizeWidth: PropTypes.number.isRequired,
    resizeHeight: PropTypes.number.isRequired,
    src: PropTypes.string.isRequired,
    // Callbacks
    onCropped: PropTypes.func.isRequired
  }
}

// Exports

module.exports = UploadImageModalPreview
